import React from 'react';
import { useTranslation } from 'react-i18next';
import { intlLocale, fmtUsd, fmtPct, fmtFixed, withSign, ltr } from '@/lib/format';

/****************
 * THE TRADER METRICS, ONE LIST. Moved out of Dashboard.tsx 2026-09-26 so the
 * Top Traders table and the trader comparison read the same definitions: a
 * metric added here appears in both, spelt the same, with the same (i).
 ****************/

// 2026-05-16 (Nick): "8.6h" → "8h40min" format. Hours + minutes, days for >24h.
export function formatDuration(hours: number) {
    const totalMin = Math.round((hours || 0) * 60);
    if (totalMin <= 0) return '—';
    if (totalMin < 60) return `${totalMin}min`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h >= 24) {
        const d = Math.floor(h / 24);
        const remH = h % 24;
        return remH > 0 ? `${d}d ${remH}h` : `${d}d`;
    }
    return m > 0 ? `${h}h${m}min` : `${h}h`;
}

export function formatROI(roi: number, pnl: number) {
    if (Math.abs(roi) < 0.1 && Math.abs(pnl) > 0) {
        return ltr(`${roi >= 0 ? '+' : '-'}<${fmtPct(0.1, 1)}`);
    }
    return `${withSign(roi >= 0, fmtPct(roi, 1))}`;
}

/****************
 * Top Traders customizable columns. Every metric shown on a trader's profile is
 * available here as a column: pick which ones to display, sort by any of them,
 * and filter traders on min/max values. 2026-08 (Nick).
 ****************/

export const usd = (v: number, digits = 0) =>
    fmtUsd(v, digits, { minDigits: 0 });

export type TraderColumn = {
  /** One plain-language line, shown in the picker. Every column has one, so the
   *  list reads evenly instead of some rows explaining themselves and others
   *  saying nothing. `info` stays the long form behind the (i). Named `blurb`
   *  to match the mobile client, where `short` already means the abbreviated
   *  column label. */
  blurb: string;
    key: string;
    label: string;
    /** Sort and filter value. Null means "this trader has no such number" — it
     *  sorts last in both directions and never satisfies a min/max bound. */
    get: (t: any) => number | null;
    render: (t: any) => React.ReactNode;
    /** Lower is better (drawdown, avg loss) — affects default sort direction. */
    lowerBetter?: boolean;
    unit?: string;
    /** What this column actually measures, in one sentence. Shown as an (i) on
     *  the header and beside the name in the picker.
     *
     *  Every metric here is computed from OUR OWN trade records — the positions
     *  we scraped, closed and priced ourselves. None of it is a figure Binance
     *  published about a trader. That distinction is the point of writing these
     *  down: two products can both display "Win Rate" and mean different things,
     *  and the one that refuses to say which is the one you cannot check. */
    info?: string;
};

/** No longer an exception. Guillaume's ruling 2026-09-08: the only realised
 *  PnL visible anywhere on the site is the one we compute from our own closed
 *  trades, so every column on this page now shares one source.
 *
 *  The caveat that came with the old behaviour, kept because it has not been
 *  re-measured: our inferred figure was found to have the WRONG SIGN for 52%
 *  of the roster when it was measured, which is why the venue's number was
 *  preferred. The order repairs behind that are still in progress. Where our
 *  number is wrong it is now wrong ON SCREEN rather than masked -- which is
 *  the intent, because showing Binance's figure meant nobody could see the
 *  gap. Superseded text follows.
 *
 *  OLD: The sole exception to "every column is computed from our own trades": total
 *  PnL prefers Binance's own per-position closing_pnl where we have it, because
 *  our inferred figure had the WRONG SIGN for 52% of the roster when it was
 *  measured. It is still a sum of per-position results, not a published
 *  aggregate, and it falls back to ours where coverage is missing. */
const PNL_PROVENANCE = 'traders:col.totalPnl.info';

/** Net Position's cell: the side word is translated, so it is a component. */
function NetPositionCell({ v }: { v: number }) {
    const { t } = useTranslation('traders');
    return <span className={v >= 0 ? 'text-success' : 'text-danger'}>{v >= 0 ? t('col.netPosition.long') : t('col.netPosition.short')}{usd(Math.abs(v))}</span>;
}

/** `label`, `blurb` and `info` hold translation KEYS (traders namespace
 *  included): render them with t(). */
export const TRADER_COLUMNS: TraderColumn[] = [
    { key: 'totalPnl', blurb: 'traders:col.totalPnl.blurb', label: 'traders:col.totalPnl', unit: '$', info: PNL_PROVENANCE, get: t => t.total_pnl_usd || 0,
      render: t => <span className={(t.total_pnl_usd || 0) >= 0 ? 'text-success' : 'text-danger'}>{withSign((t.total_pnl_usd || 0) >= 0, usd(t.total_pnl_usd || 0))}</span> },
    { key: 'roi', blurb: 'traders:col.roi.blurb', label: 'traders:col.roi', unit: '%',
      info: 'traders:col.roi.info',
      get: t => t.roi_percent || 0,
      render: t => <span className={(t.roi_percent || 0) >= 0 ? 'text-success' : 'text-danger'}>{formatROI(t.roi_percent || 0, t.total_pnl_usd || 0)}</span> },
    { key: 'winRate', blurb: 'traders:col.winRate.blurb', label: 'traders:col.winRate', unit: '%',
      info: 'traders:col.winRate.info',
      get: t => t.win_rate || 0,
      render: t => fmtPct(t.win_rate || 0, 1) },
    { key: 'trades', blurb: 'traders:col.trades.blurb', label: 'traders:col.trades', get: t => t.total_trades || 0,
      render: t => (t.total_trades || 0).toLocaleString(intlLocale()) },
    // Renamed from "Max Drawdown", which is a portfolio statistic: the deepest
    // fall of an equity curve from its own peak. This is not that. It is one
    // trade's worst moment, and calling it drawdown invited every reader to
    // compare it against a number that means something else. The storage key is
    // deliberately unchanged, so a saved column choice survives the rename.
    // Verified against rest/drawdown.go: trades.drawdown_pct is the price-only
    // maximum adverse excursion, and the served value multiplies it by that
    // trade's leverage. 2026-09-03.
    // 2026-09-22: labelled "Worst Trade", this sat beside the trader page's
    // "worst single trade" -- the worst FINAL result -- and the two numbers
    // disagreed, as two different statistics must. Guillaume read it as one
    // number wrong. It is the worst drawdown, and says so; the trader page's
    // Streak & Cadence shows the same figure under the same name.
    // 2026-09-22, M-70. Guillaume: "drawup is not a real term" and drawdown is
    // not this one either. MAE is the trade-level term for exactly this
    // quantity, and the blurb beside it carries the sentence, so the acronym
    // is never the only thing on screen. Storage keys unchanged so a saved
    // column choice survives the rename, again.
    { key: 'maxDrawdown', blurb: 'traders:col.maxDrawdown.blurb', label: 'traders:col.maxDrawdown', unit: '%', lowerBetter: true,
      info: 'traders:col.maxDrawdown.info',
      // No measured trade: no figure (2026-10-13; 0% there was a default, and
      // 1,277 of 2,010 MEXC traders showed it).
      get: t => t.max_drawdown_measured === false ? null : t.max_drawdown_percent ?? 0,
      render: t => t.max_drawdown_measured === false ? '—'
        : <span className="text-danger">-{fmtPct(t.max_drawdown_percent ?? 0, 1)}</span> },
    { key: 'avgDrawdown', blurb: 'traders:col.avgDrawdown.blurb', label: 'traders:col.avgDrawdown', unit: '%', lowerBetter: true,
      info: 'traders:col.avgDrawdown.info',
      get: t => t.max_drawdown_measured === false ? null : t.avg_drawdown_percent ?? 0,
      render: t => t.max_drawdown_measured === false ? '—'
        : <span className="text-danger">-{fmtPct(t.avg_drawdown_percent ?? 0, 1)}</span> },
    { key: 'avgDuration', blurb: 'traders:col.avgDuration.blurb', label: 'traders:col.avgDuration',
      info: 'traders:col.avgDuration.info',
      get: t => t.avg_trade_duration || 0,
      render: t => t.avg_trade_duration ? formatDuration(t.avg_trade_duration) : '—' },
    { key: 'sharpe', blurb: 'traders:col.sharpe.blurb', label: 'traders:col.sharpe',
      info: 'traders:col.sharpe.info',
      get: t => t.sharpe_ratio || 0,
      render: t => fmtFixed(t.sharpe_ratio || 0, 2) },
    { key: 'sortino', blurb: 'traders:col.sortino.blurb', label: 'traders:col.sortino',
      info: 'traders:col.sortino.info',
      get: t => t.sortino_ratio || 0,
      render: t => fmtFixed(t.sortino_ratio || 0, 2) },
    // The profile's newer figures, as columns and filters (Guillaume,
    // 2026-10-20: "filter them based on these new Data like VaR and Calmar").
    // Same names as the profile; null (too little history) is a dash.
    { key: 'worstTrade', blurb: 'traders:col.worstTrade.blurb', label: 'traders:col.worstTrade', unit: '%', lowerBetter: true,
      info: 'traders:col.worstTrade.info',
      get: t => t.worst_trade_percent ?? null,
      render: t => t.worst_trade_percent == null ? '—' : <span className="text-danger">-{fmtPct(t.worst_trade_percent, 1)}</span> },
    { key: 'bestMfe', blurb: 'traders:col.bestMfe.blurb', label: 'traders:col.bestMfe', unit: '%',
      info: 'traders:col.bestMfe.info',
      get: t => t.best_mfe_percent ?? null,
      render: t => t.best_mfe_percent == null ? '—' : <span className="text-success">+{fmtPct(t.best_mfe_percent, 1)}</span> },
    { key: 'avgMfe', blurb: 'traders:col.avgMfe.blurb', label: 'traders:col.avgMfe', unit: '%',
      info: 'traders:col.avgMfe.info',
      get: t => t.avg_mfe_percent ?? null,
      render: t => t.avg_mfe_percent == null ? '—' : <span className="text-success">+{fmtPct(t.avg_mfe_percent, 1)}</span> },
    // The Performance Metrics' last row (Guillaume, 10-31: "use some of them in
    // the filtration system of the traders table"), on margin, over his trades.
    { key: 'pointOfNoReturn', blurb: 'traders:col.pointOfNoReturn.blurb', label: 'traders:col.pointOfNoReturn', unit: '%',
      info: 'traders:col.pointOfNoReturn.info',
      get: t => t.point_of_no_return ?? null,
      render: t => t.point_of_no_return == null ? '—' : <span className="text-danger">{fmtPct(t.point_of_no_return, 0)}</span> },
    { key: 'bestStopLoss', blurb: 'traders:col.bestStopLoss.blurb', label: 'traders:col.bestStopLoss', unit: '%',
      info: 'traders:col.bestStopLoss.info',
      get: t => t.best_stop_loss ?? null,
      render: t => t.best_stop_loss == null ? '—' : `-${fmtPct(t.best_stop_loss, 0)}` },
    { key: 'mfeGivenBack', blurb: 'traders:col.mfeGivenBack.blurb', label: 'traders:col.mfeGivenBack', unit: '%', lowerBetter: true,
      info: 'traders:col.mfeGivenBack.info',
      get: t => t.avg_mfe_given_back ?? null,
      render: t => t.avg_mfe_given_back == null ? '—' : <span className="text-danger">{fmtPct(t.avg_mfe_given_back, 1)}</span> },
    { key: 'profitKept', blurb: 'traders:col.profitKept.blurb', label: 'traders:col.profitKept', unit: '%',
      info: 'traders:col.profitKept.info',
      get: t => t.profit_kept ?? null,
      render: t => t.profit_kept == null ? '—' : fmtPct(t.profit_kept, 0) },
    { key: 'greenToRed', blurb: 'traders:col.greenToRed.blurb', label: 'traders:col.greenToRed', unit: '%', lowerBetter: true,
      info: 'traders:col.greenToRed.info',
      get: t => t.green_to_red ?? null,
      render: t => t.green_to_red == null ? '—' : fmtPct(t.green_to_red, 0) },
    { key: 'bestTakeProfit', blurb: 'traders:col.bestTakeProfit.blurb', label: 'traders:col.bestTakeProfit', unit: '%',
      info: 'traders:col.bestTakeProfit.info',
      get: t => t.best_take_profit ?? null,
      render: t => t.best_take_profit == null ? '—' : `+${fmtPct(t.best_take_profit, 0)}` },
    { key: 'cvar', blurb: 'traders:col.cvar.blurb', label: 'traders:col.cvar', unit: '%', lowerBetter: true,
      info: 'traders:col.cvar.info',
      get: t => t.cvar_95 ?? null,
      render: t => t.cvar_95 == null ? '—' : fmtPct(t.cvar_95, 2) },
    { key: 'calmar', blurb: 'traders:col.calmar.blurb', label: 'traders:col.calmar',
      info: 'traders:col.calmar.info',
      get: t => t.calmar ?? null,
      render: t => t.calmar == null ? '—' : fmtFixed(t.calmar, 2) },
    { key: 'equityDrawdown', blurb: 'traders:col.equityDrawdown.blurb', label: 'traders:col.equityDrawdown', unit: '%', lowerBetter: true,
      info: 'traders:col.equityDrawdown.info',
      get: t => t.equity_drawdown_percent ?? null,
      render: t => t.equity_drawdown_percent == null ? '—' : <span className="text-danger">-{fmtPct(t.equity_drawdown_percent, 1)}</span> },
    { key: 'profitableMonths', blurb: 'traders:col.profitableMonths.blurb', label: 'traders:col.profitableMonths', unit: '%',
      info: 'traders:col.profitableMonths.info',
      get: t => t.profitable_months_percent ?? null,
      render: t => t.profitable_months_percent == null ? '—' : fmtPct(t.profitable_months_percent, 0) },
    { key: 'profitFactor', blurb: 'traders:col.profitFactor.blurb', label: 'traders:col.profitFactor',
      info: 'traders:col.profitFactor.info',
      get: t => t.profit_factor || 0,
      render: t => fmtFixed(t.profit_factor || 0, 2) },
    { key: 'avgLeverage', blurb: 'traders:col.avgLeverage.blurb', label: 'traders:col.avgLeverage', unit: 'x', get: t => t.avg_leverage || 0,
      render: t => `${fmtFixed(t.avg_leverage || 0, 1)}x` },
    // Beside it, as on the profile and the session (2026-09-25).
    // The TYPICAL (median) trade's margin, as the profile leads with (Guillaume,
    // 2026-10-21): the average read $552 for a trader whose trades put up ~$60.
    { key: 'avgMargin', blurb: 'traders:col.avgMargin.blurb', label: 'traders:metric.avgMargin', unit: '$',
      info: 'traders:col.typicalMargin.info',
      get: t => t.median_margin || t.avg_margin || 0,
      render: t => (t.median_margin || t.avg_margin) ? fmtUsd(t.median_margin || t.avg_margin || 0, 2, { minDigits: 0 }) : '—' },
    { key: 'walletSize', blurb: 'traders:col.walletSize.blurb', label: 'traders:col.walletSize', unit: '$',
      info: 'traders:col.walletSize.info',
      get: t => t.wallet_size || 0,
      render: t => (t.wallet_size ? usd(t.wallet_size) : '—') },
    { key: 'activePositions', blurb: 'traders:col.activePositions.blurb', label: 'traders:col.activePositions', get: t => t.active_positions || 0,
      render: t => (t.active_positions || 0).toLocaleString(intlLocale()) },
    /* M-56, after Liquid: the open book's SHAPE, not just how many rows it
       has. Both ride on an aggregate the dashboard already runs. */
    { key: 'biggestPosition', blurb: 'traders:col.biggestPosition.blurb', label: 'traders:col.biggestPosition', unit: '$',
      info: 'traders:col.biggestPosition.info',
      get: t => t.biggest_position || 0,
      render: t => (t.biggest_position ? usd(t.biggest_position) : '\u2014') },
    { key: 'netPosition', blurb: 'traders:col.netPosition.blurb', label: 'traders:col.netPosition', unit: '$',
      info: 'traders:col.netPosition.info',
      get: t => t.net_position || 0,
      render: t => {
          const v = t.net_position || 0;
          if (!v) return '\u2014';
          return <NetPositionCell v={v} />;
      } },
    { key: 'unrealizedPnl', blurb: 'traders:col.unrealizedPnl.blurb', label: 'traders:col.unrealizedPnl', unit: '$', get: t => t.unrealized_pnl || 0,
      render: t => <span className={(t.unrealized_pnl || 0) >= 0 ? 'text-success' : 'text-danger'}>{usd(t.unrealized_pnl || 0, 2)}</span> },
    { key: 'winCount', blurb: 'traders:col.winCount.blurb', label: 'traders:col.winCount', get: t => t.win_count || 0, render: t => (t.win_count || 0).toLocaleString(intlLocale()) },
    { key: 'lossCount', blurb: 'traders:col.lossCount.blurb', label: 'traders:col.lossCount', get: t => t.loss_count || 0, render: t => (t.loss_count || 0).toLocaleString(intlLocale()) },
    { key: 'avgWin', blurb: 'traders:col.avgWin.blurb', label: 'traders:col.avgWin', unit: '$', get: t => t.avg_win || 0,
      render: t => <span className="text-success">{usd(t.avg_win || 0, 2)}</span> },
    // NOT lowerBetter: the value is negative (-$50), so "lower first" listed the
    // biggest losers first. Descending puts the smallest losses on top
    // (Guillaume, 2026-09-26).
    { key: 'avgLoss', blurb: 'traders:col.avgLoss.blurb', label: 'traders:col.avgLoss', unit: '$', get: t => t.avg_loss || 0,
      render: t => <span className="text-danger">{usd(t.avg_loss || 0, 2)}</span> },
    /* The last figure that was on the profile and not here. M-56.
       BINANCE'S OWN NUMBER since 2026-09-22: trades.total_fees, written from
       trade_histories by the venue-fee pass. It said "est." until then, and
       correctly -- our estimate ran about seventeen times the truth. Rows the
       pass has not reached yet still hold the estimate and are stamped
       fees_source='estimated'; /admin/db -> fee_rollout_coverage says how far
       it has got. Kept off the request path (backend trader_fee_totals.go). */
    { key: 'totalFees', blurb: 'traders:col.totalFees.blurb', label: 'traders:col.totalFees', unit: '$', lowerBetter: true,
      info: 'traders:col.totalFees.info',
      get: t => t.total_fees_usd || 0,
      render: t => t.total_fees_usd
        ? <span className="text-muted-foreground">{usd(t.total_fees_usd)}</span>
        : '—' },
    { key: 'totalVolume', blurb: 'traders:col.totalVolume.blurb', label: 'traders:col.totalVolume', unit: '$',
      info: 'traders:col.totalVolume.info',
      get: t => t.total_volume || 0,
      render: t => (t.total_volume ? usd(t.total_volume) : '—') },
    // Requested as a filter, and therefore built as a COLUMN: a filter whose
    // value is invisible on the row cannot be checked by eye, and an unverifiable
    // filter is indistinguishable from a broken one. Being a column, it is
    // sortable and filterable for free -- both come from the definition.
    { key: 'longShort', blurb: 'traders:col.longShort.blurb', label: 'traders:col.longShort', unit: '%',
      info: 'traders:col.longShort.info',
      get: t => (t.long_ratio ?? null),
      render: t => {
          if (t.long_ratio === null || t.long_ratio === undefined) return '—';
          const long = Math.round(t.long_ratio);
          return (
              <span className="whitespace-nowrap">
                  <span className="text-success">{long}%</span>
                  <span className="text-muted-foreground"> / </span>
                  <span className="text-danger">{100 - long}%</span>
              </span>
          );
      } },
];
